import { useState, useMemo, useCallback, useEffect } from "react";
import { can } from "../config";
import { apiCall } from "../api";
import { Input, Btn, Field } from "./common/FormElements";
import { Modal } from "./common/Modal";

function AddRevisionModal({ onSave, onClose }) {
  const [form, setForm] = useState({ revDate: "", details: "", revisedBy: "", approvedBy: "" });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!form.revDate || !form.details) { alert("Rev Date and Revision Details are required."); return; }
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  }

  return (
    <Modal title="Add Revision Entry" onClose={onClose}>
      <div className="space-y-3">
        <Input label="Rev Date (DD-MM-YYYY)" value={form.revDate} onChange={e => setForm(f => ({ ...f, revDate: e.target.value }))} placeholder="e.g. 01-04-2025" />
        <Field label="Revision Details">
          <textarea className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" rows={4} value={form.details} onChange={e => setForm(f => ({ ...f, details: e.target.value }))} placeholder="Describe what changed in this revision..." data-testid="revision-details-input" />
        </Field>
        <Input label="Revised By" value={form.revisedBy} onChange={e => setForm(f => ({ ...f, revisedBy: e.target.value }))} placeholder="Name of person revising" />
        <Input label="Approved By" value={form.approvedBy} onChange={e => setForm(f => ({ ...f, approvedBy: e.target.value }))} placeholder="Name of approver (optional)" />
      </div>
      <div className="flex gap-2 mt-4">
        <Btn onClick={handleSave} disabled={saving} testId="save-revision-btn">{saving ? "Saving..." : "Save Revision"}</Btn>
        <Btn onClick={onClose} variant="ghost">Cancel</Btn>
      </div>
    </Modal>
  );
}

export function RevisionHistory({ user, unitId }) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingApproval, setEditingApproval] = useState(null);
  const [approvalValue, setApprovalValue] = useState("");
  const canEdit = can(user, "editMachines");

  const fetchRevisions = useCallback(async () => {
    try { const data = await apiCall("GET", `/machine-revisions/${unitId}`); setRevisions(data); }
    catch (error) { console.error("Failed to fetch revisions:", error); }
    finally { setLoading(false); }
  }, [unitId]);

  useEffect(() => { fetchRevisions(); }, [fetchRevisions]);

  async function handleAddRevision(formData) {
    try { await apiCall("POST", `/machine-revisions/${unitId}`, formData); await fetchRevisions(); setShowAddModal(false); }
    catch (error) { alert("Failed to add revision"); }
  }

  async function saveApproval(revId) {
    try { await apiCall("PATCH", `/machine-revisions/${unitId}/${revId}`, { approvedBy: approvalValue }); await fetchRevisions(); setEditingApproval(null); }
    catch (error) { alert("Failed to update approval"); }
  }

  function renderDetails(details) {
    if (!details) return null;
    const parts = details.split(/(S\.NO\.\d+[-\u2013]\d+|S\.NO\.\d+|REMOVED|ADDED|INITIAL RELEASE)/g);
    return parts.map((part, i) => {
      if (/^(S\.NO\.\d+[-\u2013]\d+|S\.NO\.\d+)$/.test(part)) return <strong key={i}>{part}</strong>;
      if (/^(REMOVED|ADDED|INITIAL RELEASE)$/.test(part)) return <strong key={i}>{part}</strong>;
      return <span key={i}>{part}</span>;
    });
  }

  if (loading) return <div className="text-center py-10 text-gray-400">Loading revision history...</div>;

  return (
    <div className="space-y-4" data-testid="revision-history">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">MNT-F-01 &middot; Machine List Revision History &middot; {revisions.length} revisions</p>
        {canEdit && <Btn onClick={() => setShowAddModal(true)} testId="add-revision-btn">+ Add Revision</Btn>}
      </div>
      {showAddModal && <AddRevisionModal onSave={handleAddRevision} onClose={() => setShowAddModal(false)} />}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ borderCollapse: "collapse" }} data-testid="revision-table">
            <thead>
              <tr className="bg-gray-800 text-white">
                <th className="border border-gray-600 px-4 py-3 text-center font-bold w-20">REV.NO.</th>
                <th className="border border-gray-600 px-4 py-3 text-center font-bold w-28">REV.DATE</th>
                <th className="border border-gray-600 px-4 py-3 text-center font-bold">REVISION DETAILS</th>
                <th className="border border-gray-600 px-4 py-3 text-center font-bold w-32">REVISED BY</th>
                <th className="border border-gray-600 px-4 py-3 text-center font-bold w-32">APPROVED BY</th>
              </tr>
            </thead>
            <tbody>
              {revisions.map((rev, i) => (
                <tr key={rev.id} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"} data-testid={`revision-row-${rev.revNo}`}>
                  <td className="border border-gray-300 px-4 py-3 text-center font-bold text-gray-800">{rev.revNo}</td>
                  <td className="border border-gray-300 px-4 py-3 text-center text-gray-700">{rev.revDate}</td>
                  <td className="border border-gray-300 px-4 py-3 text-center text-gray-800 leading-relaxed">{renderDetails(rev.details)}</td>
                  <td className="border border-gray-300 px-4 py-3 text-center text-gray-700">{rev.revisedBy}</td>
                  <td className={`border border-gray-300 px-4 py-3 text-center ${canEdit ? "cursor-pointer hover:bg-blue-50" : ""}`}
                    onClick={() => { if (canEdit) { setEditingApproval(rev.id); setApprovalValue(rev.approvedBy || ""); } }}
                    data-testid={`approval-cell-${rev.revNo}`}>
                    {editingApproval === rev.id ? (
                      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                        <input type="text" className="border border-blue-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300" value={approvalValue} onChange={e => setApprovalValue(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") saveApproval(rev.id); if (e.key === "Escape") setEditingApproval(null); }} autoFocus data-testid={`approval-input-${rev.revNo}`} />
                        <button onClick={() => saveApproval(rev.id)} className="text-green-600 hover:text-green-800 text-xs font-bold px-1" data-testid={`approval-save-${rev.revNo}`}>OK</button>
                        <button onClick={() => setEditingApproval(null)} className="text-gray-400 hover:text-gray-600 text-xs px-1">X</button>
                      </div>
                    ) : (
                      <span className={`text-gray-700 ${!rev.approvedBy ? "text-gray-300 italic" : ""}`}>{rev.approvedBy || (canEdit ? "Click to set" : "-")}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
